import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Heart, Cross, Shield, Crown, CheckCircle, XCircle, Sparkles, Zap, RotateCcw, Camera, BookOpen, Infinity, Eye, AlertCircle, Volume2, VolumeX } from 'lucide-react'
import confetti from 'canvas-confetti'
import { useGameAudio } from '../hooks/useGameAudio'
import { getQuizStages, getBibleVerse, recordGamePlay } from '../utils/api'
import './Experience.css'

// Maps Lucide icon names stored in the database to their React components
const ICON_MAP = {
  Heart, Cross, Shield, Crown, CheckCircle, XCircle,
  Sparkles, Zap, RotateCcw, Camera, BookOpen, Infinity, Eye, AlertCircle,
}
const FLOATING_ITEMS = [
  { emoji: '🥚', top: '15%', left: '5%', delay: '0s', duration: '6s', size: '2.5rem' },
  { emoji: '🐰', top: '25%', right: '8%', delay: '1s', duration: '7s', size: '3rem' },
  { emoji: '🌸', top: '60%', left: '3%', delay: '2s', duration: '8s', size: '2rem' },
  { emoji: '🥚', top: '70%', right: '5%', delay: '0.5s', duration: '6.5s', size: '2.2rem' },
  { emoji: '🐣', top: '40%', left: '8%', delay: '1.5s', duration: '7.5s', size: '2.8rem' },
  { emoji: '🌷', top: '80%', right: '12%', delay: '2.5s', duration: '8s', size: '2.3rem' },
  { emoji: '🐰', top: '10%', right: '15%', delay: '3s', duration: '7s', size: '2.6rem' },
  { emoji: '✨', top: '45%', left: '2%', delay: '1.8s', duration: '5s', size: '1.8rem' },
  { emoji: '🥚', top: '85%', left: '15%', delay: '0.8s', duration: '6s', size: '2rem' },
  { emoji: '🌸', top: '20%', left: '12%', delay: '2.2s', duration: '7s', size: '1.9rem' },
]

// Deterministic celebration particles (avoid random re-renders)
const CELEBRATION_PARTICLES = [
  { emoji: '🎉', left: '5%',  delay: '0.0s', duration: '2.8s', size: '2rem'   },
  { emoji: '✨', left: '15%', delay: '0.3s', duration: '2.5s', size: '1.6rem' },
  { emoji: '🥚', left: '22%', delay: '0.6s', duration: '3.0s', size: '2.2rem' },
  { emoji: '🐰', left: '30%', delay: '0.1s', duration: '2.7s', size: '2.4rem' },
  { emoji: '🌸', left: '40%', delay: '0.4s', duration: '2.6s', size: '1.8rem' },
  { emoji: '⭐', left: '50%', delay: '0.2s', duration: '2.9s', size: '2.0rem' },
  { emoji: '💫', left: '60%', delay: '0.5s', duration: '2.5s', size: '1.9rem' },
  { emoji: '🎊', left: '68%', delay: '0.0s', duration: '3.1s', size: '2.1rem' },
  { emoji: '🌷', left: '77%', delay: '0.7s', duration: '2.7s', size: '1.7rem' },
  { emoji: '🥚', left: '85%', delay: '0.3s', duration: '2.8s', size: '2.0rem' },
  { emoji: '✨', left: '92%', delay: '0.1s', duration: '2.6s', size: '1.5rem' },
  { emoji: '🐣', left: '10%', delay: '1.0s', duration: '2.9s', size: '2.3rem' },
  { emoji: '🌸', left: '45%', delay: '1.2s', duration: '2.7s', size: '1.6rem' },
  { emoji: '🎉', left: '73%', delay: '0.9s', duration: '3.0s', size: '2.0rem' },
  { emoji: '⭐', left: '55%', delay: '1.4s', duration: '2.8s', size: '1.8rem' },
]

export default function Experience() {
  const navigate = useNavigate()
  const { playSound, startMusic, toggleMute, isMuted } = useGameAudio()

  const [currentStage, setCurrentStage] = useState(0)
  const [score, setScore] = useState(0)
  const [selectedAnswer, setSelectedAnswer] = useState(null)
  const [showResult, setShowResult] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [answeredStages, setAnsweredStages] = useState([])

  const [showTruth, setShowTruth] = useState(false)
  const [isCorrect, setIsCorrect] = useState(false)
  const [shakingAnswer, setShakingAnswer] = useState(null)

  // Verse game state
  const [showVerseGame, setShowVerseGame] = useState(false)
  const [filledBlanks, setFilledBlanks] = useState({})
  const [selectedWord, setSelectedWord] = useState(null)
  const [verseCompleted, setVerseCompleted] = useState(false)
  const [wrongAttempt, setWrongAttempt] = useState(null)
  const [justFilledBlank, setJustFilledBlank] = useState(null)

  // Content loaded from Supabase
  const [quizStages, setQuizStages] = useState([])
  const [verseGame, setVerseGame] = useState(null)
  const [loadingContent, setLoadingContent] = useState(true)
  const [contentError, setContentError] = useState(null)

  useEffect(() => {
    async function loadContent() {
      try {
        const [stages, verse] = await Promise.all([getQuizStages(), getBibleVerse()])
        setQuizStages(stages)
        setVerseGame(verse)
      } catch (err) {
        setContentError(err.message)
      } finally {
        setLoadingContent(false)
      }
    }
    loadContent()
  }, [])

  const stage = quizStages[currentStage]
  const progress = quizStages.length > 0 ? (answeredStages.length / quizStages.length) * 100 : 0

  // Start background music on first interaction
  const handleFirstInteraction = () => startMusic()

  const fireCompletionConfetti = () => {
    // Center burst
    confetti({
      particleCount: 120,
      spread: 80,
      origin: { y: 0.62 },
      colors: ['#8b5cf6', '#a855f7', '#fbbf24', '#ec4899', '#10b981'],
      disableForReducedMotion: true,
    })
    // Left cannon
    setTimeout(() => confetti({
      particleCount: 80,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.65 },
      colors: ['#c084fc', '#f59e0b', '#34d399'],
      disableForReducedMotion: true,
    }), 200)
    // Right cannon
    setTimeout(() => confetti({
      particleCount: 80,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.65 },
      colors: ['#ec4899', '#8b5cf6', '#fbbf24'],
      disableForReducedMotion: true,
    }), 350)
    // Gold star burst
    setTimeout(() => confetti({
      particleCount: 50,
      spread: 100,
      origin: { y: 0.65 },
      shapes: ['star'],
      colors: ['#fbbf24', '#f59e0b', '#fef3c7'],
      scalar: 2,
      disableForReducedMotion: true,
    }), 550)
  }

  useEffect(() => {
    if (!completed) return
    const timer = setTimeout(() => {
      fireCompletionConfetti()
    }, 150)
    return () => clearTimeout(timer)
  }, [completed])

  const handleAnswer = (index, correct) => {
    if (showResult) return
    
    playSound('select')
    setSelectedAnswer(index)
    setIsCorrect(correct)
    setShowResult(true)
    
    if (correct) {
      playSound('correct')
    } else {
      playSound('wrong')
      setShakingAnswer(index)
      setTimeout(() => setShakingAnswer(null), 500)
    }

    // Show truth panel after a short delay
    setTimeout(() => {
      setShowTruth(true)
    }, 400)
    
    if (correct && !answeredStages.includes(currentStage)) {
      setScore(s => s + 1)
      setAnsweredStages([...answeredStages, currentStage])
    }

    setTimeout(() => {
      if (currentStage < quizStages.length - 1) {
        setCurrentStage(c => c + 1)
        setSelectedAnswer(null)
        setShowResult(false)
        setShowTruth(false)
        setIsCorrect(false)
      } else {
        setCompleted(true)
        playSound('complete')
        recordGamePlay('easter_truth').catch(console.error)
      }
    }, 3500)
  }

  const handleNextGame = () => {
    setShowVerseGame(true)
  }

  const handleWordSelect = (wordObj) => {
    playSound('select')
    setSelectedWord(wordObj)
  }

  const handleBlankClick = (index, part) => {
    if (!selectedWord || !part.isBlank || filledBlanks[index]) return
    
    const isCorrect = selectedWord.word.toLowerCase() === part.correctAnswer.toLowerCase()
    
    if (isCorrect) {
      playSound('wordPlace')
      setFilledBlanks(prev => ({
        ...prev,
        [index]: { word: selectedWord.word, isCorrect: true, icon: selectedWord.icon }
      }))
      setJustFilledBlank(index)
      setTimeout(() => setJustFilledBlank(null), 500)
      setSelectedWord(null)
      setWrongAttempt(null)
      
      // Check if all blanks are correctly filled
      const allParts = verseGame.lines.flatMap(line => line.parts)
      const blankIndices = allParts.map((p, i) => p.isBlank ? i : -1).filter(i => i !== -1)
      const correctFilledCount = Object.values(filledBlanks).filter(f => f.isCorrect).length + 1
      
      if (correctFilledCount === blankIndices.length) {
        setTimeout(() => {
          setVerseCompleted(true)
          playSound('verseComplete')
          recordGamePlay('memory_verse').catch(console.error)
          // Multi-wave confetti celebration
          confetti({
            particleCount: 150,
            spread: 100,
            origin: { y: 0.6 },
            colors: ['#10b981', '#8b5cf6', '#f59e0b', '#ec4899', '#3b82f6'],
            disableForReducedMotion: true
          })
          setTimeout(() => confetti({
            particleCount: 80,
            angle: 60,
            spread: 60,
            origin: { x: 0, y: 0.65 },
            colors: ['#34d399', '#a78bfa', '#fbbf24'],
            disableForReducedMotion: true
          }), 250)
          setTimeout(() => confetti({
            particleCount: 80,
            angle: 120,
            spread: 60,
            origin: { x: 1, y: 0.65 },
            colors: ['#f9a8d4', '#8b5cf6', '#fde68a'],
            disableForReducedMotion: true
          }), 400)
          setTimeout(() => confetti({
            particleCount: 40,
            spread: 90,
            origin: { y: 0.65 },
            shapes: ['star'],
            colors: ['#fbbf24', '#f59e0b'],
            scalar: 1.8,
            disableForReducedMotion: true
          }), 600)
        }, 500)
      }
    } else {
      playSound('wordWrong')
      // Show wrong attempt feedback with hint
      setWrongAttempt({
        index,
        word: selectedWord.word,
        correctAnswer: part.correctAnswer
      })
      setSelectedWord(null)
      
      // Clear wrong attempt after 2 seconds
      setTimeout(() => setWrongAttempt(null), 2000)
    }
  }

  const resetVerseGame = () => {
    setFilledBlanks({})
    setSelectedWord(null)
    setVerseCompleted(false)
    setWrongAttempt(null)
    setJustFilledBlank(null)
  }

  const resetGame = () => {
    setCurrentStage(0)
    setScore(0)
    setAnsweredStages([])
    setCompleted(false)
    setSelectedAnswer(null)
    setShowResult(false)
    setShowTruth(false)
    setIsCorrect(false)
    setShakingAnswer(null)
    setShowVerseGame(false)
    setFilledBlanks({})
    setSelectedWord(null)
    setVerseCompleted(false)
    setWrongAttempt(null)
    setJustFilledBlank(null)
  }

  if (loadingContent) {
    return (
      <div className="experience-page immersive">
        <div className="immersive-backdrop">
          <div className="backdrop-orb orb-1" />
          <div className="backdrop-orb orb-2" />
          <div className="backdrop-orb orb-3" />
        </div>
        <main className="immersive-main">
          <div className="game-card" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <Sparkles size={40} style={{ color: '#8b5cf6', marginBottom: '1rem' }} />
            <p style={{ fontSize: '1.1rem', color: '#6b7280' }}>Loading Easter Quest…</p>
          </div>
        </main>
      </div>
    )
  }

  if (contentError) {
    return (
      <div className="experience-page immersive">
        <div className="immersive-backdrop">
          <div className="backdrop-orb orb-1" />
          <div className="backdrop-orb orb-2" />
          <div className="backdrop-orb orb-3" />
        </div>
        <main className="immersive-main">
          <div className="game-card" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <AlertCircle size={40} style={{ color: '#ef4444', marginBottom: '1rem' }} />
            <p style={{ fontSize: '1.1rem', color: '#6b7280' }}>Could not load quiz content. Please try again later.</p>
            <p style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: '0.5rem' }}>{contentError}</p>
            <button className="action-btn secondary" style={{ marginTop: '1.5rem' }} onClick={() => navigate('/')}>
              <ArrowLeft size={18} /> Go Home
            </button>
          </div>
        </main>
      </div>
    )
  }

  if (showVerseGame) {
    return (
      <div className="experience-page immersive" onClick={handleFirstInteraction}>
        <div className="immersive-backdrop">
          <div className="backdrop-orb orb-1" />
          <div className="backdrop-orb orb-2" />
          <div className="backdrop-orb orb-3" />
          {FLOATING_ITEMS.map((item, index) => (
            <div
              key={index}
              className="floating-decoration"
              style={{
                top: item.top,
                left: item.left,
                right: item.right,
                animationDelay: item.delay,
                animationDuration: item.duration,
                fontSize: item.size,
              }}
            >
              {item.emoji}
            </div>
          ))}
        </div>

        {/* Celebration rain on verse completion */}
        {verseCompleted && (
          <div className="celebration-overlay" aria-hidden="true">
            {CELEBRATION_PARTICLES.map((p, i) => (
              <div
                key={i}
                className="celebration-drop"
                style={{ left: p.left, animationDelay: p.delay, animationDuration: p.duration, fontSize: p.size }}
              >
                {p.emoji}
              </div>
            ))}
          </div>
        )}

        <header className="immersive-header">
          <button className="nav-back" onClick={() => navigate('/')}>
            <ArrowLeft size={20} />
          </button>
          <button
            className="sound-toggle"
            onClick={e => { e.stopPropagation(); toggleMute() }}
            aria-label={isMuted ? 'Unmute sounds' : 'Mute sounds'}
            title={isMuted ? 'Unmute sounds' : 'Mute sounds'}
          >
            {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>
        </header>

        <main className="immersive-main">
          <div className="game-card verse-game">
            <div className="game-header-row">
              <div className="eyebrow-pill" style={{ color: '#8b5cf6' }}>
                <BookOpen size={14} />
                MEMORY VERSE
              </div>
            </div>

            <h1 className="game-title">{verseGame.title}</h1>
            <p className="game-hint">
              <Sparkles size={18} style={{ color: '#8b5cf6' }} />
              <span>{verseGame.subtitle}</span>
            </p>

            {verseCompleted && (
              <div className="verse-completion-banner">
                <CheckCircle size={32} className="completion-check" />
                <span>Amazing! You completed the verse!</span>
              </div>
            )}

            {wrongAttempt && (
              <div className="verse-hint-banner">
                <AlertCircle size={24} className="hint-icon" />
                <span>"{wrongAttempt.word}" doesn't fit here. Try "{wrongAttempt.correctAnswer}"!</span>
              </div>
            )}

            <div className={`verse-container ${verseCompleted ? 'verse-done' : ''}`}>
              {verseGame.lines.map((line, lineIndex) => (
                <div key={lineIndex} className="verse-line">
                  {line.parts.map((part, partIndex) => {
                    const globalIndex = verseGame.lines
                      .slice(0, lineIndex)
                      .reduce((acc, l) => acc + l.parts.length, 0) + partIndex
                    return (
                      <span key={partIndex} className="verse-part">
                        {part.isBlank ? (
                          <button
                            className={`blank-slot ${filledBlanks[globalIndex] ? 'filled' : ''} ${selectedWord ? 'selectable' : ''} ${justFilledBlank === globalIndex ? 'just-filled' : ''}`}
                            onClick={() => handleBlankClick(globalIndex, part)}
                            disabled={filledBlanks[globalIndex] || !selectedWord}
                          >
                            {filledBlanks[globalIndex] ? (
                              <span className={`filled-word ${filledBlanks[globalIndex].isCorrect ? 'correct' : 'wrong'}`}>
                                {filledBlanks[globalIndex].word}
                              </span>
                            ) : (
                              <span className="blank-dash">—</span>
                            )}
                          </button>
                        ) : (
                          <span className="verse-text">{part.text}</span>
                        )}
                        {partIndex < line.parts.length - 1 && <span>&nbsp;</span>}
                      </span>
                    )
                  })}
                </div>
              ))}
            </div>

            <div className="verse-reference">{verseGame.reference}</div>

            <div className="word-options-container">
              <p className="word-options-hint">
                {selectedWord ? `Tap a blank to place "${selectedWord.word}"` : "Tap a word to select it"}
              </p>
              <div className="word-options">
                {verseGame.word_options.map((option, index) => {
                  const isUsed = Object.values(filledBlanks).some(filled => filled.word === option.word)
                  const isSelected = selectedWord?.word === option.word
                  const IconComponent = ICON_MAP[option.icon_name]
                  
                  return (
                    <button
                      key={index}
                      className={`word-option ${isUsed ? 'used' : ''} ${isSelected ? 'selected' : ''}`}
                      onClick={() => !isUsed && handleWordSelect(option)}
                      disabled={isUsed}
                      style={{ '--word-color': option.color }}
                    >
                      <div className="word-icon" style={{ background: `${option.color}20`, color: option.color }}>
                        {IconComponent && <IconComponent size={20} />}
                      </div>
                      <span className="word-text">{option.word}</span>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="game-actions">
              <button onClick={resetVerseGame} className="action-btn secondary">
                <RotateCcw size={18} />
                Reset Verse
              </button>
            </div>
          </div>
        </main>
      </div>
    )
  }

  if (completed) {
    return (
      <div className="experience-page immersive" onClick={handleFirstInteraction}>
        {/* Celebration emoji rain */}
        <div className="celebration-overlay" aria-hidden="true">
          {CELEBRATION_PARTICLES.map((p, i) => (
            <div
              key={i}
              className="celebration-drop"
              style={{ left: p.left, animationDelay: p.delay, animationDuration: p.duration, fontSize: p.size }}
            >
              {p.emoji}
            </div>
          ))}
        </div>

        <div className="immersive-backdrop">
          <div className="backdrop-orb orb-1" />
          <div className="backdrop-orb orb-2" />
          <div className="backdrop-orb orb-3" />
          {/* Floating Decorations */}
          {FLOATING_ITEMS.map((item, index) => (
            <div
              key={index}
              className="floating-decoration"
              style={{
                top: item.top,
                left: item.left,
                right: item.right,
                animationDelay: item.delay,
                animationDuration: item.duration,
                fontSize: item.size,
              }}
            >
              {item.emoji}
            </div>
          ))}
        </div>

        <header className="immersive-header">
          <button
            className="sound-toggle"
            onClick={e => { e.stopPropagation(); toggleMute() }}
            aria-label={isMuted ? 'Unmute sounds' : 'Mute sounds'}
            title={isMuted ? 'Unmute sounds' : 'Mute sounds'}
          >
            {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>
        </header>
        
        <div className="immersive-container">
          <div className="completion-card">
            <div className="completion-icon celebration-bounce">
              <Crown size={48} />
            </div>
            <h1 className="completion-heading">
              <span aria-hidden="true">🎉 </span>Amazing!<span aria-hidden="true"> 🎉</span>
            </h1>
            <p className="completion-subtitle">You completed the Easter Story Quest!</p>
            
            <div className="final-score-display">
              <div className="score-circle score-pop-in">
                <span className="score-fraction">{score}/{quizStages.length}</span>
                <span className="score-label">Correct</span>
              </div>
            </div>

            <div
              className="completion-stars"
              aria-label={`Score: ${score} out of ${quizStages.length}`}
            >
              <span aria-hidden="true">{'⭐'.repeat(score)}</span>
            </div>
            
            <p className="completion-message">
              Jesus is alive and gives us hope and eternal life!<br />
              He loves you so much! 💖
            </p>
            
            <div className="completion-actions">
              <button onClick={resetGame} className="action-btn secondary">
                <RotateCcw size={20} />
                Play Again
              </button>
              <button onClick={handleNextGame} className="action-btn primary">
                Next Game
                <ArrowLeft size={20} style={{ transform: 'rotate(180deg)' }} />
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const StageIcon = ICON_MAP[stage?.icon_name]

  return (
    <div className="experience-page immersive" onClick={handleFirstInteraction}>
      {/* Animated Backdrop */}
      <div className="immersive-backdrop">
        <div className="backdrop-orb orb-1" />
        <div className="backdrop-orb orb-2" />
        <div className="backdrop-orb orb-3" />
        {/* Floating Decorations */}
        {FLOATING_ITEMS.map((item, index) => (
          <div
            key={index}
            className="floating-decoration"
            style={{
              top: item.top,
              left: item.left,
              right: item.right,
              animationDelay: item.delay,
              animationDuration: item.duration,
              fontSize: item.size,
            }}
          >
            {item.emoji}
          </div>
        ))}
      </div>

      {/* Top Navigation */}
      <header className="immersive-header">
        <button className="nav-back" onClick={() => navigate('/')}>
          <ArrowLeft size={20} />
        </button>
        <button
          className="sound-toggle"
          onClick={e => { e.stopPropagation(); toggleMute() }}
          aria-label={isMuted ? 'Unmute sounds' : 'Mute sounds'}
          title={isMuted ? 'Unmute sounds' : 'Mute sounds'}
        >
          {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
      </header>

      {/* Truth Reveal Panel - Left Side */}
      <div className={`truth-panel ${showTruth ? 'visible' : ''}`}>
        <div className="truth-card" style={{ '--truth-color': stage.color }}>
          <div className="truth-icon-wrapper" style={{ background: `${stage.color}20`, color: stage.color }}>
            {StageIcon && <StageIcon size={28} />}
          </div>
          <div className="truth-content">
            <h3 className="truth-title">{stage.truth_title}</h3>
            <p className="truth-description">{stage.truth_description}</p>
          </div>
        </div>
      </div>

      {/* Main Immersive Card */}
      <main className="immersive-main">
        <div className="game-card">
          {/* Header Row: Eyebrow + Progress */}
          <div className="game-header-row">
            <div className="eyebrow-pill" style={{ color: stage.color }}>
              <Zap size={14} />
              INTERACTIVE GAME
            </div>
            
            <div className="progress-indicator">
              <span className="progress-percent">{Math.round(progress)}%</span>
              <div className="progress-track">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progress}%`, background: stage.color }}
                />
              </div>
            </div>
          </div>
          
          {/* Title */}
          <h1 className="game-title">Choose the right Easter truth</h1>
          
          {/* Hint */}
          <div className="game-hint">
            <Sparkles size={18} style={{ color: stage.color }} />
            <span>Tap an answer to continue the Easter journey.</span>
          </div>

          {/* Question Card */}
          <div className="question-card">
            <div className="question-meta" style={{ color: stage.color }}>
              QUESTION {currentStage + 1} OF {quizStages.length}
            </div>
            <h2 className="question-title">{stage.question}</h2>
            <p className="question-description">{stage.description}</p>
          </div>

          {/* Answer Options */}
          <div className="answers-container">
            {stage.answers.map((answer, index) => (
              <button
                key={index}
                className={`answer-pill ${
                  showResult
                    ? answer.correct
                      ? 'correct'
                      : selectedAnswer === index
                      ? 'wrong'
                      : ''
                    : ''
                } ${selectedAnswer === index && !showResult ? 'selected' : ''} ${shakingAnswer === index ? 'shake-wrong' : ''}`}
                onClick={() => handleAnswer(index, answer.correct)}
                disabled={showResult}
                style={{ 
                  '--stage-color': stage.color,
                  animationDelay: `${index * 0.1}s`
                }}
              >
                <span className="answer-text">{answer.text}</span>
                {showResult && answer.correct && (
                  <CheckCircle size={20} className="answer-status correct" />
                )}
                {showResult && selectedAnswer === index && !answer.correct && (
                  <XCircle size={20} className="answer-status wrong" />
                )}
              </button>
            ))}
          </div>

          {/* Bottom Actions */}
          <div className="game-actions">
            <button onClick={resetGame} className="action-btn secondary">
              <RotateCcw size={18} />
              Restart Game
            </button>
          </div>
        </div>
      </main>
    </div>
  )
}
